import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyThemeToDocument,
  getThemePreference,
  setThemePreference as persistThemePreference,
  THEME_STORAGE_KEY,
  type ThemePreference,
} from "../lib/theme-settings.js";
import {
  applyDensityToDocument,
  getDensityPreference,
  setDensityPreference as persistDensityPreference,
  type DensityPreference,
} from "../lib/density-settings.js";
import { patchAdminPreferences } from "../lib/auth-api.js";
import { getAdminJwt } from "../lib/auth-storage.js";
import { useOptionalAuth } from "./AuthContext.js";
import { ThemeContext } from "./theme-context.js";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const [theme, setThemeState] = useState<ThemePreference>(() => getThemePreference());
  const [density, setDensityState] = useState<DensityPreference>(() => getDensityPreference());

  const setTheme = useCallback((t: ThemePreference) => {
    setThemeState(t);
    persistThemePreference(t);
    if (getAdminJwt()) {
      void patchAdminPreferences({ themePreference: t }).catch(() => undefined);
    }
  }, []);

  const setDensity = useCallback((d: DensityPreference) => {
    setDensityState(d);
    persistDensityPreference(d);
  }, []);

  // Verdikt Graphite is a single (dark) theme — always applied.
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    applyDensityToDocument(density);
  }, [density]);

  // Sync from server-stored admin preference (still honoured for back-compat,
  // though every value renders the graphite theme).
  useEffect(() => {
    const pref = user?.themePreference;
    if (!pref) return;
    queueMicrotask(() => {
      setThemeState(pref as ThemePreference);
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(THEME_STORAGE_KEY, pref);
      }
      applyThemeToDocument(pref as ThemePreference);
    });
  }, [user?.id, user?.themePreference]);

  const value = useMemo(
    () => ({ theme, setTheme, density, setDensity }),
    [theme, setTheme, density, setDensity]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
